import { useInfiniteQuery } from '@tanstack/react-query';
import api from '@/lib/api';
import { queryKeys } from '@/lib/query-keys';

export interface RecipeSummary {
  recipeId: string;
  recipeType: string;
  name: string;
  description: string | null;
  level: number;
  requiresLearned: boolean;
  inputs: Array<{ itemName: string; quantity: number }>;
  outputs: Array<{ itemName: string; quantity: number; probability?: number }>;
  constraints: Record<string, unknown>;
  isEnabled: boolean;
  metadata: Record<string, unknown>;
  createdAt: string;
  updatedAt: string;
}

interface UseRecipesOptions {
  type?: string;
  search?: string;
  enabled?: boolean;
  admin?: boolean;
}

interface UseRecipesReturn {
  recipes: RecipeSummary[];
  total: number;
  loading: boolean;
  error: string | null;
  refresh: () => void;
  loadMore: () => void;
  hasMore: boolean;
}

const LIMIT = 50;

export function useRecipes(options: UseRecipesOptions = {}): UseRecipesReturn {
  const filters: Record<string, unknown> = {};
  if (options.type) filters.type = options.type;
  if (options.search) filters.search = options.search;
  if (options.enabled !== undefined) filters.enabled = options.enabled;
  if (options.admin) filters.admin = options.admin;

  const { data, isLoading, error, refetch, fetchNextPage, hasNextPage, isFetchingNextPage } =
    useInfiniteQuery({
      queryKey: queryKeys.crafting.recipes(filters),
      queryFn: async ({ pageParam = 0 }) => {
        const params: Record<string, string> = {};
        if (options.type) params.type = options.type;
        if (options.search) params.search = options.search;
        if (options.enabled !== undefined) params.enabled = String(options.enabled);
        params.limit = String(LIMIT);
        params.offset = String(pageParam);

        const path = options.admin ? '/arches/crafting/admin/recipes' : '/arches/crafting/recipes';
        const { data } = await api.get<{ recipes: RecipeSummary[]; total: number }>(path, {
          params,
        });
        return data;
      },
      initialPageParam: 0,
      getNextPageParam: (lastPage, allPages) => {
        const next = allPages.length * LIMIT;
        return next < lastPage.total ? next : undefined;
      },
    });

  const recipes = data?.pages.flatMap((p) => p.recipes) ?? [];
  const total = data?.pages[0]?.total ?? 0;

  return {
    recipes,
    total,
    loading: isLoading,
    error: error instanceof Error ? error.message : error ? 'Failed to load recipes' : null,
    refresh: () => {
      refetch();
    },
    loadMore: () => {
      if (hasNextPage && !isFetchingNextPage) fetchNextPage();
    },
    hasMore: !!hasNextPage,
  };
}
